// Blog service layer for admin operations
import { supabase } from '@/integrations/supabase/client';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  featured_image: string | null;
  category: string | null;
  tags: string[] | null;
  language: string;
  status: 'draft' | 'published';
  published_at: string | null;
  author_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface GetBlogPostsParams {
  search?: string;
  status?: string;
  language?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface GetBlogPostsResult {
  data: BlogPost[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Generate slug from title
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens
    .trim()
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

// Get blog posts with filters, pagination, and sorting
export async function getBlogPosts(params: GetBlogPostsParams = {}): Promise<GetBlogPostsResult> {
  const {
    search,
    status,
    language,
    page = 1,
    pageSize = 20,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = params;

  let query = supabase
    .from('blog_posts')
    .select('*', { count: 'exact' });

  // Apply status filter
  if (status && status !== 'all') {
    query = query.eq('status', status);
  }

  // Apply language filter
  if (language && language !== 'all') {
    query = query.eq('language', language);
  }

  // Apply search filter (title)
  if (search) {
    query = query.ilike('title', `%${search}%`);
  }

  // Apply sorting
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  // Apply pagination
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;
  query = query.range(start, end);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }

  return {
    data: (data || []) as BlogPost[],
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.ceil((count || 0) / pageSize),
  };
}

// Get single blog post by ID
export async function getBlogPostById(id: string): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }

  return data as BlogPost | null;
}

// Get blog post by slug (for public pages)
export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();

  if (error) {
    console.error('Error fetching blog post by slug:', error);
    throw error;
  }

  return data as BlogPost | null;
}

// Create new blog post
export async function createBlogPost(postData: {
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  featured_image?: string;
  category?: string;
  tags?: string[];
  language: string;
  status: 'draft' | 'published';
  published_at?: string;
}): Promise<BlogPost> {
  const { data: userData } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('blog_posts')
    .insert({
      ...postData,
      author_id: userData.user?.id,
      published_at: postData.status === 'published' ? (postData.published_at || new Date().toISOString()) : null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }

  return data as BlogPost;
}

// Update blog post
export async function updateBlogPost(
  id: string,
  postData: Partial<{
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    featured_image: string;
    category: string;
    tags: string[];
    language: string;
    status: 'draft' | 'published';
    published_at: string;
  }>
): Promise<BlogPost> {
  // If status is changing to published and no published_at, set it now
  const updateData = { ...postData };
  if (postData.status === 'published' && !postData.published_at) {
    updateData.published_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('blog_posts')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }

  return data as BlogPost;
}

// Delete blog post
export async function deleteBlogPost(id: string): Promise<void> {
  const { error } = await supabase
    .from('blog_posts')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
}

// Duplicate blog post
export async function duplicateBlogPost(id: string): Promise<BlogPost> {
  const originalPost = await getBlogPostById(id);
  
  if (!originalPost) {
    throw new Error('Post not found');
  }

  const newSlug = `${originalPost.slug}-copy-${Date.now()}`;
  
  return createBlogPost({
    title: `${originalPost.title} (Copy)`,
    slug: newSlug,
    excerpt: originalPost.excerpt || undefined,
    content: originalPost.content,
    featured_image: originalPost.featured_image || undefined,
    category: originalPost.category || undefined,
    tags: originalPost.tags || undefined,
    language: originalPost.language,
    status: 'draft',
  });
}

// Get blog stats
export async function getBlogStats() {
  const { count: totalCount } = await supabase
    .from('blog_posts')
    .select('*', { count: 'exact', head: true });

  const { count: publishedCount } = await supabase
    .from('blog_posts')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'published');

  const { count: draftCount } = await supabase
    .from('blog_posts')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'draft');

  const { count: englishCount } = await supabase
    .from('blog_posts')
    .select('*', { count: 'exact', head: true })
    .eq('language', 'en');

  const { count: spanishCount } = await supabase
    .from('blog_posts')
    .select('*', { count: 'exact', head: true })
    .eq('language', 'es');

  return {
    total: totalCount || 0,
    published: publishedCount || 0,
    draft: draftCount || 0,
    english: englishCount || 0,
    spanish: spanishCount || 0,
  };
}

// Get published posts for public blog page
export async function getPublishedPosts(params: {
  language?: string;
  category?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<GetBlogPostsResult> {
  const { language, category, page = 1, pageSize = 10 } = params;

  let query = supabase
    .from('blog_posts')
    .select('*', { count: 'exact' })
    .eq('status', 'published')
    .order('published_at', { ascending: false });

  if (language && language !== 'all') {
    query = query.eq('language', language);
  }

  if (category) {
    query = query.eq('category', category);
  }

  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;
  query = query.range(start, end);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching published posts:', error);
    throw error;
  }

  return {
    data: (data || []) as BlogPost[],
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.ceil((count || 0) / pageSize),
  };
}

// Categories for blog posts
export const BLOG_CATEGORIES = [
  'Immigration',
  'Criminal Defense',
  'Personal Injury',
  'Family Law',
  'Tax',
  'Probate & Estate',
  'Real Estate',
  'Business Law',
  'News',
  'Legal Tips',
];

// Languages
export const BLOG_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
];

// Status options
export const BLOG_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'published', label: 'Published', color: 'bg-green-100 text-green-800' },
];
